// ============================================
// PortfolioBreakdownPane
//
// Right column of the Portfolio workspace. Collapsible to a thin
// vertical bar via the chevron at its top edge — same pattern as
// the right-side panels in CBookPage / DOM Trader.
//
// Composition (top → bottom, expanded state):
//   • Header strip: chevron toggle + label
//   • Page-level Period selector (drives both the breakdown values
//     and stays in sync with the TopBar Cards Period selector via
//     PortfolioStatsContext.cardsPeriod)
//   • Platform selector: All / MT5 (Master) / cTrader (Master)
//   • Portfolio (parent) summary card — Net of Costs
//   • B-Book / A-Book / C-Book (child cards, indented)
//
// Visibility is controlled by the parent (ChartWorkspace) which also
// auto-collapses this pane when "Get Insight" opens the AI panel
// inside the chart container.
// ============================================

import { ChevronRight, ChevronLeft } from 'lucide-react';

import { CardsPeriodSelector } from './CardsPeriodSelector';
import { PlatformSelector, type PortfolioPlatform } from './PlatformSelector';
import { BreakdownGrid } from './BreakdownGrid';
import type { PlatformMasters } from '@/hooks/usePlatformMasters';
import { usePortfolioStats, fmtHdrMoney, pnlColor } from '@/stores/PortfolioStatsContext';

interface Props {
  collapsed:    boolean;
  onToggle:     () => void;
  platform:     PortfolioPlatform;
  onPlatform:   (p: PortfolioPlatform) => void;
  masters:      PlatformMasters;
}

export function PortfolioBreakdownPane({
  collapsed,
  onToggle,
  platform,
  onPlatform,
  masters,
}: Props) {
  const {
    bbook, abook, cbook, total, lastUpdated, vsPriorMonth,
    bbookBySource, abookBySource, cbookBySource,
    pricing,
  } = usePortfolioStats();

  // ── Collapsed state: 28px vertical strip with chevron + rotated label
  if (collapsed) {
    return (
      <div
        className="h-full flex flex-col items-center justify-start py-2 border-l border-[#3a3a3c] cursor-pointer hover:bg-[#252527] transition-colors"
        style={{ backgroundColor: '#1e1e20', width: 28 }}
        onClick={onToggle}
        title="Expand Portfolio breakdown"
      >
        <ChevronLeft className="w-4 h-4 text-[#aaa]" />
        <div
          className="text-[10px] uppercase tracking-widest text-[#aaa] mt-3"
          style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
        >
          Portfolio
        </div>
      </div>
    );
  }

  // ── Expanded state
  // Width tuned for: 130px label rail + 4 cards × ~140px each + paddings.
  // The label rail is sized to fit the longest row label ("NET UNRL P/L",
  // "COMMISSIONS") on a single line at 13px font; narrower forces wrap.
  return (
    <div
      className="h-full flex flex-col overflow-hidden border-l border-[#3a3a3c] flex-shrink-0"
      style={{ backgroundColor: '#1e1e20', width: 720 }}
    >
      {/* Header strip — title + collapse chevron */}
      <div
        className="px-3 py-2 border-b border-[#3a3a3c] flex items-center justify-between flex-shrink-0"
        style={{ backgroundColor: '#252527' }}
      >
        <div className="text-[11px] uppercase tracking-wider text-white">Portfolio</div>
        <button
          onClick={onToggle}
          className="text-[#aaa] hover:text-white transition-colors"
          title="Collapse Portfolio breakdown"
          aria-label="Collapse Portfolio breakdown"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* Selectors strip — Period + Platform on the same line */}
      <div className="px-3 py-2 border-b border-[#3a3a3c] flex items-center gap-4 flex-shrink-0">
        <CardsPeriodSelector />
        <PlatformSelector value={platform} onChange={onPlatform} masters={masters} />
        <div
          className="ml-auto text-[10px] uppercase tracking-wider whitespace-nowrap"
          style={{ color: '#8a8a8a' }}
          title="Time of the most recent portfolio snapshot. On weekends and market holidays this is the last business-day reading — values do not update while markets are closed."
        >
          Updated {lastUpdated ?? '—'}
        </div>
      </div>

      {/* Breakdown body — single CSS grid: label rail + 4 card columns,
          with a unified row template at the parent level so every row
          ("Net Real P/L" etc.) sits at the identical y-coordinate across
          the rail and all 4 cards. Reading horizontally → compare a
          metric across books. Reading down a card → full breakdown of
          that book. Cards keep their identity via per-column borders +
          accent stripes (Portfolio yellow, B/A/C teal). */}
      <div className="flex-1 min-h-0 overflow-y-auto p-2">
        <BreakdownGrid
          total={total}
          bbook={bbook}
          abook={abook}
          cbook={cbook}
          platform={platform}
          bySource={{ B: bbookBySource, A: abookBySource, C: cbookBySource }}
          pricing={pricing}
        />

        {/* ── Under-total slim row: This Month vs prior month, NET REALIZED.
            Pace gauge — are we ahead of or behind last month at the same
            point? `vsPriorMonth.total` is prior-MTD net realized; we compare
            it to this period's net realized (total.realized). Shown only when
            the backend supplies the comparison (period=month). ── */}
        {vsPriorMonth?.available && (() => {
          const thisVal = total.realized;
          const prior   = vsPriorMonth.total;
          const delta   = thisVal != null ? thisVal - prior : null;
          const up      = delta != null && delta >= 0;
          return (
            <div
              className="mt-2 px-2 py-1.5 rounded flex items-center gap-3 text-[11px]"
              style={{ backgroundColor: '#1b1b1d', border: '1px solid #3a3a3c' }}
              title={`Net Realized pace vs the same point last month (${vsPriorMonth.from} → ${vsPriorMonth.to}). This: ${thisVal != null ? fmtHdrMoney(thisVal) : '—'} · Prior: ${fmtHdrMoney(prior)}`}
            >
              <span className="uppercase tracking-wider" style={{ color: '#8a8a8a' }}>
                vs Prior Month · Net Realized{platform !== 'All' ? ' · Both platforms' : ''}
              </span>
              {thisVal != null && (
                <span className="font-mono" style={{ color: pnlColor(thisVal) }}>
                  this {fmtHdrMoney(thisVal)}
                </span>
              )}
              <span style={{ color: '#5a5a5a' }}>·</span>
              <span className="font-mono" style={{ color: '#8a8a8a' }}>
                prior {fmtHdrMoney(prior)}
              </span>
              {delta != null && (
                <span
                  className="ml-auto font-mono inline-flex items-center gap-1"
                  style={{ color: up ? '#6aaa78' : '#d07070' }}
                >
                  {up ? '▲' : '▼'} {fmtHdrMoney(Math.abs(delta))}
                  <span style={{ color: '#8a8a8a' }}>{up ? 'ahead' : 'behind'}</span>
                </span>
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );
}